import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react'
import { AppState, ViewState, ProgressState, ReviewReport, LogEntry, ChatMessage, ChatState } from '@/types'

/**
 * Action types for the app reducer
 */
type AppAction =
  | { type: 'SET_VIEW_STATE'; payload: ViewState }
  | { type: 'SET_SELECTED_FILE'; payload: File | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PROGRESS'; payload: Partial<ProgressState> }
  | { type: 'ADD_LOG'; payload: LogEntry }
  | { type: 'SET_RESULTS'; payload: ReviewReport }
  | { type: 'RESET' }
  | { type: 'SET_CUSTOM_JURISDICTIONS'; payload: string[] }
  | { type: 'SET_CUSTOM_CONTRACT_TYPES'; payload: string[] }
  | { type: 'SET_CUSTOM_ROLES'; payload: Record<string, { name: string; instructions: string }> }
  | { type: 'SET_CONTRACT_TEXT'; payload: string | null }
  | { type: 'TOGGLE_CHAT' }
  | { type: 'SET_CHAT_OPEN'; payload: boolean }
  | { type: 'SET_CHAT_THREAD_ID'; payload: string }
  | { type: 'SET_CHAT_MESSAGES'; payload: ChatMessage[] }
  | { type: 'ADD_CHAT_MESSAGE'; payload: ChatMessage }
  | { type: 'SET_CHAT_LOADING'; payload: boolean }
  | { type: 'CLEAR_CHAT' }
  | { type: 'TOGGLE_LEFT_PANEL' }

/**
 * Initial progress state
 */
const initialProgressState: ProgressState = {
  message: 'Initializing...',
  step: undefined,
  progress: 0,
  isConnected: false,
  logs: [],
}

/**
 * Initial chat state
 */
const initialChatState: ChatState = {
  isOpen: false,
  threadId: null,
  messages: [],
  isLoading: false,
}

/**
 * Initial app state
 */
const initialAppState: AppState = {
  viewState: 'empty',
  selectedFile: null,
  customJurisdictions: [],
  customContractTypes: [],
  customRoles: {},
  customRoleInstructions: {},
  progress: initialProgressState,
  results: null,
  error: null,
  contractText: null,
  chat: initialChatState,
  isLeftPanelOpen: true,
}

/**
 * App reducer
 */
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_VIEW_STATE':
      return { ...state, viewState: action.payload }

    case 'SET_SELECTED_FILE':
      return { ...state, selectedFile: action.payload }

    case 'SET_ERROR':
      return { ...state, error: action.payload, viewState: action.payload ? 'error' : state.viewState }

    case 'SET_PROGRESS':
      return {
        ...state,
        progress: { ...state.progress, ...action.payload },
        viewState: 'progress',
      }

    case 'ADD_LOG':
      return {
        ...state,
        progress: {
          ...state.progress,
          logs: [...state.progress.logs, action.payload],
        },
      }

    case 'SET_RESULTS':
      return {
        ...state,
        results: action.payload,
        viewState: 'results',
        error: null,
      }

    case 'RESET':
      return {
        ...initialAppState,
        customJurisdictions: state.customJurisdictions,
        customContractTypes: state.customContractTypes,
        customRoles: state.customRoles,
        customRoleInstructions: state.customRoleInstructions,
      }

    case 'SET_CUSTOM_JURISDICTIONS':
      return { ...state, customJurisdictions: action.payload }

    case 'SET_CUSTOM_CONTRACT_TYPES':
      return { ...state, customContractTypes: action.payload }

    case 'SET_CUSTOM_ROLES':
      return { ...state, customRoles: action.payload }

    case 'SET_CONTRACT_TEXT':
      return { ...state, contractText: action.payload }

    case 'TOGGLE_CHAT':
      return { ...state, chat: { ...state.chat, isOpen: !state.chat.isOpen } }

    case 'SET_CHAT_OPEN':
      return { ...state, chat: { ...state.chat, isOpen: action.payload } }

    case 'SET_CHAT_THREAD_ID':
      return { ...state, chat: { ...state.chat, threadId: action.payload } }

    case 'SET_CHAT_MESSAGES':
      return { ...state, chat: { ...state.chat, messages: action.payload } }

    case 'ADD_CHAT_MESSAGE':
      return { ...state, chat: { ...state.chat, messages: [...state.chat.messages, action.payload] } }

    case 'SET_CHAT_LOADING':
      return { ...state, chat: { ...state.chat, isLoading: action.payload } }

    case 'CLEAR_CHAT':
      return { ...state, chat: { ...initialChatState, isOpen: state.chat.isOpen } }

    case 'TOGGLE_LEFT_PANEL':
      return { ...state, isLeftPanelOpen: !state.isLeftPanelOpen }

    default:
      return state
  }
}

/**
 * Context type
 */
interface AppContextType {
  state: AppState
  dispatch: React.Dispatch<AppAction>
  setViewState: (viewState: ViewState) => void
  setSelectedFile: (file: File | null) => void
  setError: (error: string | null) => void
  setProgress: (progress: Partial<ProgressState>) => void
  addLog: (message: string, step?: string) => void
  setResults: (results: ReviewReport) => void
  reset: () => void
  setContractText: (text: string | null) => void
  toggleChat: () => void
  setChatOpen: (isOpen: boolean) => void
  setChatThreadId: (threadId: string) => void
  setChatMessages: (messages: ChatMessage[]) => void
  addChatMessage: (message: ChatMessage) => void
  setChatLoading: (isLoading: boolean) => void
  clearChat: () => void
  toggleLeftPanel: () => void
}

const AppContext = createContext<AppContextType | undefined>(undefined)

/**
 * App Provider component
 */
export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialAppState)

  const setViewState = useCallback((viewState: ViewState) => {
    dispatch({ type: 'SET_VIEW_STATE', payload: viewState })
  }, [])

  const setSelectedFile = useCallback((file: File | null) => {
    dispatch({ type: 'SET_SELECTED_FILE', payload: file })
  }, [])

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error })
  }, [])

  const setProgress = useCallback((progress: Partial<ProgressState>) => {
    dispatch({ type: 'SET_PROGRESS', payload: progress })
  }, [])

  const addLog = useCallback((message: string, step?: string) => {
    dispatch({ type: 'ADD_LOG', payload: { timestamp: new Date(), message, step } })
  }, [])

  const setResults = useCallback((results: ReviewReport) => {
    dispatch({ type: 'SET_RESULTS', payload: results })
  }, [])

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  const setContractText = useCallback((text: string | null) => {
    dispatch({ type: 'SET_CONTRACT_TEXT', payload: text })
  }, [])

  const toggleChat = useCallback(() => {
    dispatch({ type: 'TOGGLE_CHAT' })
  }, [])

  const setChatOpen = useCallback((isOpen: boolean) => {
    dispatch({ type: 'SET_CHAT_OPEN', payload: isOpen })
  }, [])

  const setChatThreadId = useCallback((threadId: string) => {
    dispatch({ type: 'SET_CHAT_THREAD_ID', payload: threadId })
  }, [])

  const setChatMessages = useCallback((messages: ChatMessage[]) => {
    dispatch({ type: 'SET_CHAT_MESSAGES', payload: messages })
  }, [])

  const addChatMessage = useCallback((message: ChatMessage) => {
    dispatch({ type: 'ADD_CHAT_MESSAGE', payload: message })
  }, [])

  const setChatLoading = useCallback((isLoading: boolean) => {
    dispatch({ type: 'SET_CHAT_LOADING', payload: isLoading })
  }, [])

  const clearChat = useCallback(() => {
    dispatch({ type: 'CLEAR_CHAT' })
  }, [])

  const toggleLeftPanel = useCallback(() => {
    dispatch({ type: 'TOGGLE_LEFT_PANEL' })
  }, [])

  const value: AppContextType = {
    state,
    dispatch,
    setViewState,
    setSelectedFile,
    setError,
    setProgress,
    addLog,
    setResults,
    reset,
    setContractText,
    toggleChat,
    setChatOpen,
    setChatThreadId,
    setChatMessages,
    addChatMessage,
    setChatLoading,
    clearChat,
    toggleLeftPanel,
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

/**
 * Hook to use the app context
 */
export function useApp() {
  const context = useContext(AppContext)
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}
